import flatten from '@opten/array/flatten';
import { copy } from '@opten/object/copy';
import removeProperty from '@opten/object/remove-property';
import { BehaviorSubject, empty, forkJoin, from, Observable, of as observableOf, of, Subject } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { HttpErrorResponse } from '../httpclient/httpclient';
import { Basket, BasketItem, BasketProductItem, BasketProductVariantItem } from '../models/basket';
import { Location } from '../models/customer';
import { Address } from '../models/profile';
import { shopService } from '../shop/shop-context';
import { ObservableStorage } from './observable-storage';

export const BASKET_VERSION = '2.1.0';

export class BasketStorage extends ObservableStorage<Basket> {
	onReinitBasketFull: Subject<Basket> = new Subject<Basket>();
	onBasketUpdate: BehaviorSubject<Basket>;

	constructor() {
		super('basket', localStorage);
		super
			.get()
			.pipe(
				take(1),
				switchMap(basket => {
					if (basket) {
						basket.termsAndConditions = false;
					}
					this.onBasketUpdate = new BehaviorSubject<Basket>(basket);
					return super.change();
				})
			)
			.subscribe(basket => {
				if (basket) {
					this.onBasketUpdate.next(basket);
				}
			});
	}

	updateFullBasket(updatedBasket: Basket): Observable<Basket> {
		return super.get().pipe(
			mergeMap(basket => {
				const oldBasket = copy(basket) as Basket;
				return this.getUpdatedFinalizedBasket(oldBasket, updatedBasket).pipe(
					mergeMap(basket => {
						super.set(basket);
						return observableOf(basket);
					})
				);
			})
		);
	}

	updateProductVariant(selectedProduct: BasketProductItem, location: Location): Observable<Basket> {
		return super.get().pipe(
			mergeMap(basket => {
				const oldBasket = copy(basket) as Basket;
				return this.updateBasket([selectedProduct], location, basket).pipe(
					mergeMap(updatedBasket => {
						return this.getUpdatedFinalizedBasket(oldBasket, updatedBasket).pipe(
							mergeMap(basket => {
								super.set(basket);
								return observableOf(basket);
							})
						);
					})
				);
			})
		);
	}

	addBulk(selectedProducts: BasketProductItem[], location: Location): Observable<Basket> {
		return super.get().pipe(
			mergeMap(basket => {
				const oldBasket = copy(basket) as Basket;
				return this.updateBasket(selectedProducts, location, basket, true).pipe(
					mergeMap(updatedBasket => {
						return this.getUpdatedFinalizedBasket(oldBasket, updatedBasket);
					}),
					mergeMap(basket => {
						super.set(basket);
						return of(basket);
					}),
					take(1)
				);
			})
		);
	}

	reinitBasket(): Observable<Basket> {
		return this.clearBasket(true);
	}

	clearBasket(clearFull = false): Observable<Basket> {
		return super.get().pipe(
			mergeMap(basket => {
				if (clearFull) {
					basket = null;
					this.onReinitBasketFull.next(basket);
				}
				basket = {
					items: [],
					centralWarehouseOptions: {
						floorShipping: null,
						floorShippingActive: false,
						returnOfEmpties: null,
						returnOfEmptiesActive: false,
						shippingNotification: null,
						shippingNotificationActive: false
					},
					paymentMethod: 'invoice',
					shippingMethod: '0'
				};
				super.set(basket);
				return observableOf(basket);
			})
		);
	}

	private getCentralWarehouseAddress(): Address {
		return {
			street: '',
			zip: '',
			location: ''
		} as Address; //set first standort here if available
	}

	private getGuestLocationEmpty(): Location {
		// TODO: is this correct wiht the array
		return {
			id: -1,
			billingAddresses: [{ company: '', street: '', zip: '', location: '' }],
			shippingAddresses: [{ company: '', street: '', zip: '', location: '' }],
			billingAddress: { company: '', street: '', zip: '', location: '' },
			shippingAddress: { company: '', street: '', zip: '', location: '' },
			shippingIsBillingAddress: true,
			guestEmail: '',
			guestPhone: ''
		};
	}

	private getUpdatedFinalizedBasket(oldBasket: Basket, updatedBasket: Basket): Observable<Basket> {
		oldBasket = this.getNormalizedBasket(oldBasket);
		if (this.isEqualObject(flatten(oldBasket.items.map(i => i.products)), flatten(updatedBasket.items.map(i => i.products)))) {
			return of(updatedBasket);
		} else {
			return of(updatedBasket);
		}
	}

	getNormalizedBasket(basket: Basket): Basket {
		basket = basket || { items: [], paymentMethod: '', version: BASKET_VERSION };
		basket.version = basket.items && basket.items.length > 0 ? basket.version : BASKET_VERSION;
		basket.items = basket.items || [];
		basket.basketPrices = basket.basketPrices || {
			freightCost: null,
			totalPrice: null,
			goodsCost: null
		};
		if (basket.items.length <= 1) {
			basket.customerName = basket.customerName || '';
			basket.message = basket.message || '';
			basket.centralWarehouse = basket.centralWarehouse || false;
			basket.centralWarehouseAddress = basket.centralWarehouseAddress || this.getCentralWarehouseAddress();
			basket.centralWarehouseOptions = basket.centralWarehouseOptions || {
				returnOfEmpties: null,
				returnOfEmptiesActive: false,
				shippingNotification: '',
				shippingNotificationActive: false,
				floorShipping: '',
				floorShippingActive: false
			};
			basket.termsAndConditions = false;
			basket.paymentMethod = basket.paymentMethod || 'invoice';
			basket.shippingMethod = basket.shippingMethod || '0';
		}

		return basket;
	}

	private updateBasket(
		selectedProducts: BasketProductItem[],
		location: Location,
		basket: Basket,
		updateVariantAddBulk = false
	): Observable<Basket> {
		basket = this.getNormalizedBasket(basket);
		if ((basket.items.length <= 1 && !location) || location.id === -1) {
			location = this.getGuestLocationEmpty();
		}
		const orderType = selectedProducts[0].orderType;

		const equalsLocation = (item: BasketItem, existing: BasketItem) =>
			item.location.id === existing.location.id && item.orderType === existing.orderType;
		const updateLocation = (item: BasketItem, existing: BasketItem, doUpdate: boolean) =>
			this.pushOrUpdate(item.products, existing.products, equalsProduct, updateProduct, doUpdate, invalidateProduct);
		const invalidateLocation = (newItem: { location: Location; products: BasketProductItem[] }) => newItem.products.length <= 0;

		const equalsProduct = (item: BasketProductItem, existing: BasketProductItem) => item.id === existing.id;
		const updateProduct = (item: BasketProductItem, existing: BasketProductItem, doUpdate: boolean) =>
			this.pushOrUpdate(item.variants, existing.variants, equalsVariant, updateVariant, doUpdate, invalidateVariant);
		const invalidateProduct = (newItem: BasketProductItem) => newItem.variants.length <= 0;

		const equalsVariant = (item: BasketProductVariantItem, existing: BasketProductVariantItem) => item.id === existing.id;
		const updateVariant = (item: BasketProductVariantItem, existing: BasketProductVariantItem, doUpdate: boolean) =>
			this.updateProductVariantFn(item, existing, doUpdate, updateVariantAddBulk);
		const invalidateVariant = (newItem: BasketProductVariantItem) => newItem.quantity <= 0;

		if (!location.name && basket.shippingLocations && basket.shippingLocations.length > 0) {
			const selectedLocation = basket.shippingLocations.find(o => o.id === location.id);
			if (selectedLocation) {
				location.name = selectedLocation.name;
				location.addressPreview = selectedLocation.addressPreview || selectedLocation.name;
			}
		}
		return this.pushOrUpdate(
			[
				{
					location,
					products: selectedProducts,
					orderType,
					options:
						location != null && location.id > 0
							? {
									returnOfEmpties: null,
									shippingNotification: null
							  }
							: {}
				} as BasketItem
			],
			basket.items,
			equalsLocation,
			updateLocation,
			true,
			invalidateLocation
		).pipe(map(() => basket));
		// return new Observable<Basket>(obs => {
		// 	return this.pushOrUpdate(
		// 		[
		// 			{
		// 				location,
		// 				products: selectedProducts,
		// 				orderType,
		// 				options:
		// 					location != null && location.id > 0
		// 						? {
		// 								returnOfEmpties: null,
		// 								shippingNotification: null
		// 						  }
		// 						: {}
		// 			} as BasketItem
		// 		],
		// 		basket.items,
		// 		equalsLocation,
		// 		updateLocation,
		// 		true,
		// 		invalidateLocation
		// 	).subscribe(
		// 		() => obs.next(basket),
		// 		() => void 0,
		// 		() => obs.complete()
		// 	);
		// });
	}

	private updateProductVariantFn(
		item: BasketProductVariantItem,
		existing: BasketProductVariantItem,
		doUpdate: boolean,
		updateVariantAddBulk = false
	): Observable<boolean> {
		if (doUpdate) {
			updateVariantAddBulk ? (existing.quantity += item.quantity) : (existing.quantity = item.quantity);
		}
		if (existing.quantity > 0 && (doUpdate || existing.price === undefined || existing.price === null)) {
			return this.updateProductVariantPrice(existing);
		} else {
			return observableOf(true);
		}
	}

	private updateProductVariantPrice(existing: BasketProductVariantItem): Observable<boolean> {
		return shopService.getPrice(existing.articleNumber, existing.quantity, existing.amountUomId).pipe(
			mergeMap(productVariantPriceModel => {
				if (productVariantPriceModel) {
					existing.price = productVariantPriceModel.price;
					existing.promotionPrice = productVariantPriceModel.promotionPrice;
					existing.promotionType = productVariantPriceModel.promotionType;
				}
				return observableOf(true);
			}),
			catchError((error: HttpErrorResponse) => {
				console.error(error);
				return observableOf(true);
			})
		);
	}

	private pushOrUpdate<T>(
		newArray: T[],
		existingArray: T[],
		equals: (item: T, found: T) => boolean,
		updateFn: (item: T, found: T, doUpdate: boolean) => Observable<boolean>,
		updateRequired = true,
		invalidateFn?: (item: T) => boolean
	) {
		if (!newArray) {
			return of(null);
		}
		return forkJoin(
			from(newArray).pipe(
				concatMap(item => {
					let found = existingArray.findIndex(p => equals(item, p));
					let doUpdate = updateRequired;
					if (found < 0) {
						existingArray.push(item);
						found = existingArray.findIndex(p => equals(item, p));
						doUpdate = false;
					}
					return updateFn(item, existingArray[found], doUpdate).pipe(
						mergeMap(o => {
							if (invalidateFn && invalidateFn(existingArray[found])) {
								existingArray.splice(found, 1);
							}
							return observableOf(o);
						})
					);
				})
			)
		).pipe(mergeMap(o => observableOf(true)));
	}

	getOrderModel(basket: Basket): Observable<Basket> {
		const propertiesToDelete = [
			'paymentMethods',
			'fullPriceTotal',
			'$index',
			'hasAnyProductsWithImage',
			'subPriceTotal',
			'hasImage',
			'priceTotal',
			'price',
			'image',
			'addresses',
			'shippingLocations',
			'addressPreview',
			'submitError',
			'submitSuccess',
			'shippingIsBillingAddress'
		];
		propertiesToDelete.forEach(propertyToDelete => {
			basket = removeProperty(basket, propertyToDelete) as Basket;
		});
		return of(basket);
	}

	private isEqualObject(value: any, other: any): boolean {
		// Get the value type
		const type = Object.prototype.toString.call(value);

		// If the two objects are not the same type, return false
		if (type !== Object.prototype.toString.call(other)) return false;

		// If items are not an object or array, return false
		if (['[object Array]', '[object Object]'].indexOf(type) < 0) return false;

		// Compare the length of the length of the two items
		const valueLen = type === '[object Array]' ? value.length : Object.keys(value).length;
		const otherLen = type === '[object Array]' ? other.length : Object.keys(other).length;
		if (valueLen !== otherLen) return false;

		// Compare two items
		const compare = (item1, item2) => {
			// Get the object type
			const itemType = Object.prototype.toString.call(item1);

			// If an object or array, compare recursively
			if (['[object Array]', '[object Object]'].indexOf(itemType) >= 0) {
				if (!this.isEqualObject(item1, item2)) return false;
			}

			// Otherwise, do a simple comparison
			else {
				// If the two items are not the same type, return false
				if (itemType !== Object.prototype.toString.call(item2)) return false;

				// Else if it's a function, convert to a string and compare
				// Otherwise, just compare
				if (itemType === '[object Function]') {
					if (item1.toString() !== item2.toString()) return false;
				} else {
					if (item1 !== item2) return false;
				}
			}
		};

		// Compare properties
		if (type === '[object Array]') {
			for (let i = 0; i < valueLen; i++) {
				if (compare(value[i], other[i]) === false) return false;
			}
		} else {
			for (const key in value) {
				if (value.hasOwnProperty(key)) {
					if (compare(value[key], other[key]) === false) return false;
				}
			}
		}

		// If nothing failed, return true
		return true;
	}
}
